import './index.css';
import './tailwind.css';
import './dayjs';

import { RootErrorBoundary } from '@shared/components/RootErrorBoundary';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { Providers } from '@/providers';
import { installConsoleErrorCapture, reportCrash } from '@/services/crashReporting';

// EWM-1055: console.error is lint-allowed and used in 41 places here, all of them previously
// reporting to the user's devtools and nowhere else. Installed before render so the auth and
// initialization paths - which run first and are where the useful ones live - are covered.
installConsoleErrorCapture();

// EWM-954: browser-level failures outside the React boundary report through the same sink as the
// error boundary, so the team's alert rules and KQL runbooks see all of them. The App Insights
// client arrives later (the provider hands it over once the SDK loads); reportCrash queues until
// then, so a crash this early is delivered rather than dropped (EWM-1051).
window.addEventListener('error', (event) => {
  // Resource load failures (img/script) also raise 'error' but carry no Error object; they are
  // noise here and would drown the real crashes.
  if (!event.error) return;
  reportCrash(event.error, {
    source: 'window-onerror',
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

window.addEventListener('unhandledrejection', (event) => {
  reportCrash(event.reason, { source: 'unhandled-rejection' });
});

// EWM-1055: the listener above returns early when there is no Error object, which is exactly the
// shape of a failed resource load - so a script or stylesheet that 404s was reported nowhere. The
// usual cause is a cached index.html asking for a hashed chunk a newer deploy replaced: the user
// gets a blank page and we get silence. Images stay excluded, as the comment above intends.
//
// Registered in the capture phase because resource 'error' events do not bubble - a listener on
// window never sees them otherwise. `event.target` is the failing element here and `window` for a
// script error, so the two listeners cannot both report the same event.
window.addEventListener(
  'error',
  (event) => {
    const target = event.target;
    const url =
      target instanceof HTMLScriptElement
        ? target.src
        : target instanceof HTMLLinkElement
          ? target.href
          : null;
    if (!url) return;

    reportCrash(new Error(`Resource failed to load: ${url}`), { source: 'resource-load', url });
  },
  true,
);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/*
      EWM-1100: above <Providers /> on purpose. The router's own boundary
      (defaultErrorComponent={GenericError}) covers everything inside a route, but the thirteen
      providers wrapping the router render outside it - a throw there unmounts the whole tree with
      no screen and no telemetry, which is what EWM-1081 did.
    */}
    <RootErrorBoundary reportCrash={reportCrash}>
      <Providers />
    </RootErrorBoundary>
  </StrictMode>,
);
